import { GlobalTagBadge } from "../tags/GlobalTagBadge";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

import {
  canTransition,
  deriveBoardColumns,
  getStageLabel,
  summarizeAdminTurn,
} from "@/marketing/lib/campaignPipeline";
import { extractApiErrorMessage } from "@/marketing/lib/extractApiErrorMessage";
import { getStageToneStyle } from "@/marketing/lib/pipelineTone";
import { cn } from "@/marketing/lib/utils";
import {
  type CampaignInfluencer,
  REJECTED_DOT,
  REJECTED_PILL,
} from "@/marketing/models/campaignInfluencer";
import type {
  CampaignPipelineDefinition,
  PipelineStageDefinition,
} from "@/marketing/models/campaignPipeline";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { useCampaignQuietStore } from "@/marketing/stores/useCampaignQuietStore";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { applyQuiet } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/applyQuiet";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { MultiSelectFilter } from "@/marketing/components/influencer/filters/MultiSelectFilter";
import { compareCreatorScores } from "@/marketing/models/creatorScore";

import { CreatorCard } from "./CreatorCard";
import { downloadColumnCsv } from "./downloadColumnCsv";
import { filterCreatorsByTags, keepActiveTagIds } from "./filterCreatorsByTags";
import { PipelineColumn } from "./PipelineColumn";
import { useCampaignInfluencerTagLookup } from "../tags/useCampaignInfluencerTagLookup";
import { CreatorScoreCutoffLegend } from "../score/CreatorScoreCutoffLegend";
import { useCreatorScores } from "../score/CreatorScoreProvider";

interface PipelineBoardProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
  creators: CampaignInfluencer[];
  onCardClick: (creator: CampaignInfluencer, visibleQueue?: CampaignInfluencer[]) => void;
  onAdd: (stage: string) => void;
}

const OTHER_COLUMN_ID = "__other__";
const NO_TAGS_FILTER_VALUE = "__no_tags__";

// Holds creators whose stage is not part of the campaign's pipeline definition; read-only.
const OTHER_COLUMN_STAGE: PipelineStageDefinition = {
  stage: OTHER_COLUMN_ID,
  labels: { admin: "Other", creator: "Other" },
  tone: "neutral",
  milestones: [],
};

export function PipelineBoard({
  campaignId,
  pipelineDefinition,
  creators,
  onCardClick,
  onAdd,
}: PipelineBoardProps) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const transitionBy = currentUser?.email ?? "unknown";
  const isQuiet = useCampaignQuietStore((s) => s.isQuiet(campaignId));

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [rejectedOpen, setRejectedOpen] = useState(false);
  const [rejectedDropOver, setRejectedDropOver] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [includeUntagged, setIncludeUntagged] = useState(false);
  const [scoreSort, setScoreSort] = useState("desc");
  const { scores, isLoading: isScoreLoading, isError: hasScoreError } = useCreatorScores();
  const canSortScores = !isScoreLoading && !hasScoreError;

  const {
    tagLabelById: tagNameById,
    tagNameById: rawTagNameById,
    globalTagIds,
    isReady: isTagCatalogReady,
  } = useCampaignInfluencerTagLookup(campaignId);
  const tagFilterOptions = useMemo(
    () => [
      ...(isTagCatalogReady ? [{ value: NO_TAGS_FILTER_VALUE, label: "No tags" }] : []),
      ...[...rawTagNameById].map(([tagId, name]) => ({
        value: tagId,
        label: (
          <span className="tw-inline-flex tw-items-center tw-gap-1">
            {name}{globalTagIds.has(tagId) && <GlobalTagBadge />}
          </span>
        ),
      })),
    ],
    [rawTagNameById, globalTagIds, isTagCatalogReady],
  );

  const activeSelectedTagIds = useMemo(
    () => keepActiveTagIds(selectedTagIds, tagNameById),
    [selectedTagIds, tagNameById],
  );

  const filteredCreators = useMemo(
    () => filterCreatorsByTags(creators, activeSelectedTagIds, {
      include: includeUntagged && isTagCatalogReady,
      tagNameById,
    }),
    [creators, activeSelectedTagIds, includeUntagged, isTagCatalogReady, tagNameById],
  );

  const orderedCreators = useMemo(() => {
    if (!canSortScores || scoreSort === "default") return filteredCreators;
    return [...filteredCreators].sort((first, second) => compareCreatorScores(
      first.userId == null ? null : scores[first.userId]?.finalScore,
      second.userId == null ? null : scores[second.userId]?.finalScore,
      scoreSort === "desc",
    ));
  }, [filteredCreators, scoreSort, scores, canSortScores]);

  const board = useMemo(
    () => deriveBoardColumns(pipelineDefinition, orderedCreators),
    [pipelineDefinition, orderedCreators],
  );

  const adminTurn = useMemo(
    () => summarizeAdminTurn(pipelineDefinition, creators),
    [pipelineDefinition, creators],
  );

  const draggingCreator = useMemo(
    () => creators.find((c) => c.influencerId === draggingId) ?? null,
    [creators, draggingId],
  );

  const getTransition = (creator: CampaignInfluencer, toStage: string) => {
    // A rejected creator dropped on their own stage is a plain restore.
    if (creator.rejected && creator.pipelineStage === toStage) {
      return { allowed: true, requiresFastTrack: false };
    }
    return canTransition(pipelineDefinition, creator.pipelineStage, toStage);
  };

  const transitionMutation = useMutation({
    mutationFn: ({
      influencerId,
      toStage,
      requiresFastTrack,
    }: {
      influencerId: string;
      toStage: string;
      requiresFastTrack: boolean;
    }) =>
      campaignPipelineAPIClient.transitionCampaignInfluencerStage(
        campaignId,
        influencerId,
        applyQuiet(
          {
            toStage,
            transitionBy,
            ...(requiresFastTrack && { fastTrack: true }),
          },
          isQuiet,
        ),
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      toast.success(
        `Moved to ${getStageLabel(pipelineDefinition, vars.toStage)}`,
      );
    },
    onError: (err: unknown) =>
      toast.error(extractApiErrorMessage(err, "Stage change failed")),
  });

  const rejectMutation = useMutation({
    mutationFn: ({
      influencerId,
      rejected,
    }: {
      influencerId: string;
      rejected: boolean;
    }) =>
      campaignPipelineAPIClient.setCampaignInfluencerRejected(
        campaignId,
        influencerId,
        {
          rejected,
          transitionBy,
        },
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      toast.success(
        vars.rejected ? "Marked rejected" : "Restored from rejected",
      );
    },
    onError: (err: unknown) =>
      toast.error(extractApiErrorMessage(err, "Stage change failed")),
  });

  const handleDrop = (toStage: string, influencerId: string) => {
    const creator = creators.find((c) => c.influencerId === influencerId);
    if (!creator) return;
    if (creator.pipelineStage === toStage && !creator.rejected) return;
    const { allowed, requiresFastTrack } = getTransition(creator, toStage);
    if (!allowed) return;
    if (creator.rejected) {
      // Restore from rejected first, then transition if needed.
      rejectMutation.mutate(
        { influencerId, rejected: false },
        {
          onSuccess: () => {
            if (creator.pipelineStage !== toStage) {
              transitionMutation.mutate({
                influencerId,
                toStage,
                requiresFastTrack,
              });
            }
          },
        },
      );
      return;
    }
    transitionMutation.mutate({ influencerId, toStage, requiresFastTrack });
  };

  const handleRejectDrop = (influencerId: string) => {
    const creator = creators.find((c) => c.influencerId === influencerId);
    if (!creator || creator.rejected) return;
    rejectMutation.mutate({ influencerId, rejected: true });
  };

  const handleRestore = (influencerId: string) => {
    rejectMutation.mutate({ influencerId, rejected: false });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {adminTurn.waitingOnAdmin > 0 && (
        <div
          data-testid="admin-turn-summary"
          className="tw-px-8 tw-pt-2 tw-text-xs tw-text-muted-foreground"
        >
          <span className="tw-font-semibold tw-tabular-nums tw-text-foreground">
            {adminTurn.waitingOnAdmin}
          </span>{" "}
          waiting on admin
          {adminTurn.overdue > 0 && (
            <>
              {" · "}
              <span className="tw-font-semibold tw-tabular-nums tw-text-red-600">
                {adminTurn.overdue}
              </span>{" "}
              <span className="tw-text-red-600">overdue</span>
            </>
          )}
        </div>
      )}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-8 tw-pt-2 tw-text-sm">
        <label>
          Score order{" "}
          <select
            className="tw-rounded tw-border tw-bg-background tw-p-2 disabled:tw-opacity-50"
            value={canSortScores ? scoreSort : "default"}
            disabled={!canSortScores}
            onChange={(event) => setScoreSort(event.target.value)}
          >
            <option value="default">Workflow order</option>
            <option value="desc">Highest first</option>
            <option value="asc">Lowest first</option>
          </select>
        </label>
        <span className="tw-text-xs tw-text-muted-foreground">Within each stage · Missing scores last</span>
        {tagFilterOptions.length > 0 && (
          <MultiSelectFilter
            label="Filter by tag"
            options={tagFilterOptions}
            selected={[
              ...activeSelectedTagIds,
              ...(includeUntagged && isTagCatalogReady ? [NO_TAGS_FILTER_VALUE] : []),
            ]}
            onSelectionChange={(values) => {
              setIncludeUntagged(values.includes(NO_TAGS_FILTER_VALUE));
              setSelectedTagIds(values.filter((value) => value !== NO_TAGS_FILTER_VALUE));
            }}
          />
        )}
        {hasScoreError && <span role="status" className="tw-text-muted-foreground">Scores unavailable; campaign actions remain available.</span>}
        {!hasScoreError && isScoreLoading && <span role="status" className="tw-text-muted-foreground">Scores loading; campaign actions remain available.</span>}
      </div>
      <div className="tw-overflow-x-auto tw-px-8 tw-pb-2 tw-pt-2">
        <div className="tw-flex tw-w-max tw-gap-3">
          {board.columns.map(({ stage, creators: columnCreators }) => (
            <PipelineColumn
              key={stage.stage}
              campaignId={campaignId}
              pipelineDefinition={pipelineDefinition}
              stage={stage}
              toneStyle={getStageToneStyle(pipelineDefinition, stage.stage)}
              creators={columnCreators}
              draggingId={draggingId}
              canDrop={
                draggingCreator === null ||
                getTransition(draggingCreator, stage.stage).allowed
              }
              canAdd={
                stage.stage === pipelineDefinition.initialStage ||
                canTransition(
                  pipelineDefinition,
                  pipelineDefinition.initialStage,
                  stage.stage,
                ).allowed
              }
              onDragStart={(creator) => setDraggingId(creator.influencerId)}
              onDragEnd={() => setDraggingId(null)}
              onDrop={handleDrop}
              onCardClick={(creator) => onCardClick(creator, columnCreators)}
              onAdd={onAdd}
              tagNameById={tagNameById}
              tagCatalogReady={isTagCatalogReady}
            />
          ))}

          {board.other.length > 0 && (
            <PipelineColumn
              campaignId={campaignId}
              pipelineDefinition={pipelineDefinition}
              stage={OTHER_COLUMN_STAGE}
              toneStyle={getStageToneStyle(pipelineDefinition, OTHER_COLUMN_ID)}
              creators={board.other}
              draggingId={draggingId}
              canDrop={false}
              canAdd={false}
              onDragStart={(creator) => setDraggingId(creator.influencerId)}
              onDragEnd={() => setDraggingId(null)}
              onDrop={handleDrop}
              onCardClick={onCardClick}
              onAdd={onAdd}
              tagNameById={tagNameById}
              tagCatalogReady={isTagCatalogReady}
              testId={`pipeline-column-${OTHER_COLUMN_ID}`}
            />
          )}

          {/* Collapsible Rejected column */}
          <div
            role="region"
            aria-label="Rejected creators"
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              if (!rejectedDropOver) setRejectedDropOver(true);
            }}
            onDragLeave={() => setRejectedDropOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setRejectedDropOver(false);
              const id = e.dataTransfer.getData("text/influencerId");
              if (id) handleRejectDrop(id);
            }}
            className={cn(
              "tw-flex tw-flex-shrink-0 tw-flex-col tw-rounded-xl tw-border tw-border-rose-100 tw-bg-rose-50/40 tw-transition-[width,colors]",
              rejectedOpen ? "tw-w-[260px]" : "tw-w-14",
              rejectedDropOver && "tw-border-rose-300 tw-bg-rose-50",
            )}
          >
            <button
              type="button"
              onClick={() => setRejectedOpen((v) => !v)}
              className={cn(
                "tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-rose-100 tw-px-2 tw-py-2.5 tw-text-left hover:tw-bg-rose-50",
                !rejectedOpen && "tw-flex-col tw-gap-2 tw-px-1",
              )}
            >
              {rejectedOpen ? (
                <ChevronDown className="tw-h-3.5 tw-w-3.5 tw-text-rose-600" />
              ) : (
                <ChevronRight className="tw-h-3.5 tw-w-3.5 tw-text-rose-600" />
              )}
              <span
                className={cn(
                  "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold",
                  REJECTED_PILL,
                  !rejectedOpen && "tw-rotate-180 tw-[writing-mode:vertical-rl]",
                )}
              >
                <span
                  className={cn("tw-h-1.5 tw-w-1.5 tw-rounded-full", REJECTED_DOT)}
                />
                Rejected
              </span>
              <span className="tw-rounded-full tw-border tw-border-rose-200 tw-bg-background tw-px-1.5 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-rose-700">
                {board.rejected.length}
              </span>
            </button>

            {rejectedOpen && (
              <div className="tw-flex tw-flex-col tw-gap-1.5 tw-overflow-y-auto tw-p-2">
                {board.rejected.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      downloadColumnCsv(
                        "Rejected",
                        board.rejected,
                        tagNameById,
                        pipelineDefinition,
                      )
                    }
                    disabled={!isTagCatalogReady}
                    aria-label="Download Rejected as CSV"
                    title={
                      isTagCatalogReady
                        ? "Download Rejected as CSV"
                        : "Waiting for tags to load…"
                    }
                    className="tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-border tw-border-rose-200 tw-bg-background tw-px-2 tw-py-1 tw-text-[11px] tw-font-medium tw-text-rose-700 hover:tw-bg-rose-50 disabled:tw-pointer-events-none disabled:tw-opacity-40"
                  >
                    <Download className="tw-h-3 tw-w-3" />
                    Download CSV
                  </button>
                )}
                {board.rejected.length === 0 ? (
                  <div className="tw-flex tw-h-14 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-dashed tw-border-rose-200 tw-text-[11px] tw-text-rose-700/70">
                    Drag a card here to reject
                  </div>
                ) : (
                  board.rejected.map((c) => (
                    <div key={c.influencerId} className="tw-flex tw-flex-col tw-gap-1">
                      <CreatorCard
                        campaignId={campaignId}
                        pipelineDefinition={pipelineDefinition}
                        creator={c}
                        isDragging={draggingId === c.influencerId}
                        onDragStart={() => setDraggingId(c.influencerId)}
                        onDragEnd={() => setDraggingId(null)}
                        onClick={() => onCardClick(c, board.rejected)}
                      />
                      <button
                        type="button"
                        onClick={() => handleRestore(c.influencerId)}
                        className="tw-text-[11px] tw-text-rose-700 hover:tw-underline"
                      >
                        Restore
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <CreatorScoreCutoffLegend />
    </div>
  );
}
